import { Alert } from '@/components/ui/alert';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { GetAllItems, type IItemList } from '@/pages/ItemEyes/api/ItemEyes.api';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, Loader2, Search } from 'lucide-react';
import React, { memo, useCallback, useRef, useState } from 'react';

interface VirtualizedComboboxProps {
  searchPlaceholder?: string;
  onValueChange?: (value: IItemList | null) => void;
  width?: string;
  value: IItemList | null;
  isLoading?: boolean;
}

export const AutoCompleteItems = memo(
  ({
    searchPlaceholder = 'ابحث عن صنف',
    width = 'w-full',
    value,
    onValueChange,
    isLoading = false,
  }: VirtualizedComboboxProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const parentRef = useRef<HTMLDivElement>(null);

    // Use a single, shared query key for all items across the app
    const {
      data: allItems = [],
      isLoading: isItemsLoading,
      error,
    } = useQuery({
      queryKey: ['allItems'], // Single shared key
      queryFn: () => GetAllItems(),
      staleTime: 5 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch when component mounts if data exists
    });

    // Simple filtering logic with proper null checks
    const filteredItems = React.useMemo(() => {
      // Ensure allItems is always an array
      const safeAllItems = Array.isArray(allItems) ? allItems : [];

      if (!searchTerm || searchTerm.length < 2) {
        return safeAllItems.slice(0, 50); // Show first 50 items when no search
      }

      return safeAllItems
        .filter((item) => {
          // Add null checks for item properties
          if (!item || !item.Name || !item.ID) return false;

          const nameMatch = item.Name.toLowerCase().includes(
            searchTerm.toLowerCase(),
          );
          const idMatch = item.ID.toString().includes(searchTerm);
          return nameMatch || idMatch;
        })
        .slice(0, 100); // Limit to 100 results
    }, [allItems, searchTerm]);

    const virtualizer = useVirtualizer({
      count: filteredItems.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 50, // Estimate row height
      overscan: 5,
    });

    const handleSelect = useCallback(
      (currentValue: IItemList) => {
        onValueChange?.(currentValue);
        setSearchTerm(''); // Clear search when item is selected
      },
      [onValueChange],
    );

    const handleSearchChange = useCallback((search: string) => {
      setSearchTerm(search);
    }, []);

    const isDataLoading = isLoading || isItemsLoading;

    return (
      <Command className={cn('flex flex-col gap-2', width)}>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <CommandInput
            value={searchTerm}
            onValueChange={handleSearchChange}
            placeholder={searchPlaceholder}
            className="h-11 w-full rounded-md border pr-10 focus:ring-2 focus:ring-primary"
          />
        </div>
        <CommandList>
          <div
            ref={parentRef}
            className="mt-2 max-h-64 overflow-y-auto rounded-md border bg-background"
            style={{ minHeight: '3rem' }}
          >
            {isDataLoading && (
              <div className="flex h-16 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            {error && (
              <Alert variant="outline" className="my-2">
                حدث خطأ أثناء تحميل الأصناف: {error.message}
              </Alert>
            )}
            {!isDataLoading && !error && (
              <CommandEmpty>
                {searchTerm.length >= 2 ? (
                  <Alert variant="soft" className="my-2">
                    لا توجد نتائج للبحث
                  </Alert>
                ) : (
                  <Alert variant="soft" className="my-2">
                    اكتب على الأقل حرفين للبحث
                  </Alert>
                )}
              </CommandEmpty>
            )}
            {!isDataLoading && !error && (
              <CommandGroup
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const item = filteredItems[virtualItem.index];
                  return (
                    <CommandItem
                      key={item?.ID || virtualItem.key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      value={`${item?.Name}-${item?.ID}`}
                      onSelect={() => handleSelect(item)}
                      className={cn(
                        'flex w-full cursor-pointer items-center justify-between px-3 py-2 outline-none hover:bg-muted focus:bg-muted',
                        value?.ID === item?.ID && 'bg-primary/10',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Check
                          className={cn(
                            'h-4 w-4 text-primary',
                            value?.ID === item?.ID
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <div className="flex flex-col">
                          <span className="text-base font-medium">
                            {item?.Name || 'صنف غير محدد'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            السعر: {item?.Price || 0} ج.م
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        #{item?.ID || 'N/A'}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </div>
        </CommandList>
      </Command>
    );
  },
);
